import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Plus, X } from "lucide-react";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Json } from "@/integrations/supabase/types";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";

interface KeyResult {
  description: string;
  target: string;
}

interface OKRFormData {
  objective: string;
  description: string;
  keyResults: KeyResult[];
}

interface OKRCreationFormProps {
  onSuccess: () => void;
}

const OKRCreationForm = ({ onSuccess }: OKRCreationFormProps) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const guidanceId = searchParams.get('guidanceId');
  const actionId = searchParams.get('actionId');

  const form = useForm<OKRFormData>({
    defaultValues: {
      objective: "",
      description: "",
      keyResults: [{ description: "", target: "" }],
    },
  });

  // Fetch guidance details if available
  useEffect(() => {
    const fetchGuidanceDetails = async () => {
      if (!guidanceId || !actionId) return;

      const { data: action } = await supabase
        .from('suggested_actions')
        .select('action_text')
        .eq('id', actionId)
        .single();

      if (action) {
        form.setValue('objective', action.action_text);
        form.setValue('description', `This OKR was created based on career guidance to help achieve the next level.`);
      }
    };

    fetchGuidanceDetails();
  }, [guidanceId, actionId, form]);

  const createOKRMutation = useMutation({
    mutationFn: async (formData: OKRFormData) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        // Show auth dialog if not authenticated
        throw new Error('Authentication required to save OKR');
      }

      const artifactData: {
        created_at: null;
        data: Json;
        guidance_action_id: string | null;
        guidance_id: string | null;
        name: string;
        status: string;
        type: string;
        user_id: string;
      } = {
        created_at: null,
        data: formData as unknown as Json,
        guidance_action_id: actionId,
        guidance_id: guidanceId,
        name: formData.objective,
        status: 'active',
        type: 'okr',
        user_id: user.id,
      };

      const { data: artifact, error } = await supabase
        .from('artifacts')
        .insert(artifactData)
        .select()
        .single();

      if (error) throw error;
      return artifact;
    },
    onSuccess: () => {
      toast.success("OKR created successfully!");
      onSuccess();
    },
    onError: (error) => {
      if (error.message === 'Authentication required to save OKR') {
        // Show auth dialog
        navigate('/auth', { 
          state: { 
            returnTo: window.location.pathname + window.location.search,
            message: "Please sign in to save your OKR"
          }
        });
      } else {
        toast.error("Failed to create OKR. Please try again.");
        console.error("Error creating OKR:", error);
      }
    },
  });

  const onSubmit = (data: OKRFormData) => {
    createOKRMutation.mutate(data);
  };

  const addKeyResult = () => {
    const currentKeyResults = form.getValues("keyResults");
    form.setValue("keyResults", [...currentKeyResults, { description: "", target: "" }]);
  };

  const removeKeyResult = (index: number) => {
    const currentKeyResults = form.getValues("keyResults");
    form.setValue(
      "keyResults",
      currentKeyResults.filter((_, i) => i !== index)
    );
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <FormField
          control={form.control}
          name="objective"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Objective</FormLabel>
              <FormControl>
                <Input placeholder="What do you want to achieve?" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Description</FormLabel>
              <FormControl>
                <Textarea
                  placeholder="Provide more context about your objective..."
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold">Key Results</h3>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={addKeyResult}
              disabled={form.getValues("keyResults").length >= 3}
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Key Result
            </Button>
          </div>

          {form.getValues("keyResults").map((_, index) => (
            <div key={index} className="space-y-4 p-4 border border-border rounded-lg">
              <div className="flex justify-between items-start">
                <h4 className="text-sm font-medium">Key Result {index + 1}</h4>
                {index > 0 && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => removeKeyResult(index)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>

              <FormField
                control={form.control}
                name={`keyResults.${index}.description`}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description</FormLabel>
                    <FormControl>
                      <Input placeholder="What will you measure?" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name={`keyResults.${index}.target`}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Target</FormLabel>
                    <FormControl>
                      <Input placeholder="What's your goal? (e.g., '80%', '5 times')" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          ))}
        </div>

        <div className="flex justify-end gap-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => window.history.back()}
          >
            Cancel
          </Button>
          <Button type="submit" disabled={createOKRMutation.isPending}>
            Save OKR
          </Button>
        </div>
      </form>
    </Form>
  );
};

export default OKRCreationForm;